import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User,
  Hash,
  Building2,
  Briefcase,
  Shield,
  FileCheck,
  FileClock,
  KeyRound,
  Bell,
  FileText,
  Image,
  FileSpreadsheet,
  Star,
  Clock,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import dashboardService from '../../services/dashboard.service';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import Spinner from '../../components/ui/Spinner';
import { ROUTES } from '../../config/routes.config';
import { useAuthStore } from '../../store/authStore';
import { usePortalAccess } from '../../hooks/usePortalAccess';
import { formatDocumentDisplayName } from '../../utils/documentName';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => dashboardService.getStats(),
    refetchInterval: 60000,
  });

  const latestFilesQuery = useQuery({
    queryKey: ['dashboard', 'latest-files'],
    queryFn: () => dashboardService.getLatestFiles(),
    refetchInterval: 60000,
  });

  const favoritesQuery = useQuery({
    queryKey: ['dashboard', 'favorites'],
    queryFn: () => dashboardService.getFavorites(),
    refetchInterval: 60000,
  });

  useEffect(() => {
    document.title = 'RRMS | Dashboard';
  }, []);

  useEffect(() => {
    if (statsQuery.error) {
      const message = (statsQuery.error as any)?.response?.data?.message;
      toast.error(message || 'Failed to load dashboard stats.');
    }
  }, [statsQuery.error]);

  const stats = statsQuery.data?.data;
  const profile = stats?.profile;
  const { user, role } = useAuthStore();
  const { canApprove } = usePortalAccess();

  const displayName = profile?.name || user?.name || null;
  const displayDesignation = profile?.designation || null;
  const displayDepartment = profile?.department || user?.department || null;
  const displayKgid = profile?.kgid || user?.kgid || null;
  // const displayDivision =
  //   profile?.division ??
  //   (user?.division_id != null ? `Division ${user.division_id}` : null);
  const displayDivision =
  profile?.division ??
  user?.division ??                               // ← add this line
  (user?.division_id != null ? `Division ${user.division_id}` : null);
  const displayRole =
    profile?.role_title || user?.role_title || (role ?? null);
  const hasAnyProfileData =
    !!displayName ||
    !!displayDesignation ||
    !!displayDepartment ||
    !!displayKgid ||
    !!displayDivision ||
    !!displayRole;

  useEffect(() => {
    if (stats?.profile) {
      if (stats.profile.designation) {
        localStorage.setItem(
          'rrms_profile_designation',
          stats.profile.designation
        );
      }
      if (stats.profile.kgid) {
        localStorage.setItem('rrms_profile_kgid', stats.profile.kgid);
      }
    }
  }, [stats?.profile]);

  function formatFileSize(bytes?: number | null): string {
    if (bytes == null || Number.isNaN(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f2f5',
        padding: 'clamp(12px, 3vw, 20px) clamp(12px, 3vw, 16px)',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {/* Page title row */}
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: '26px',
              fontWeight: 700,
              color: '#0f172a',
              letterSpacing: '-0.01em',
            }}
          >
            Dashboard
          </h1>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '14px',
              color: '#64748b',
              fontWeight: 400,
            }}
          >
            Welcome back, {stats?.profile?.name || 'User'}
          </p>
        </div>

        {/* Profile card */}
        {hasAnyProfileData && (
          <div
            style={{
              background:
                'linear-gradient(135deg, #1a3a6b 0%, #0d2244 100%)',
              borderRadius: '16px',
              padding: '24px 28px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(26,58,107,0.25)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '220px',
                height: '220px',
                borderRadius: '50%',
                border: '44px solid rgba(255,255,255,0.04)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '-40px',
                right: '120px',
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                border: '28px solid rgba(200,151,58,0.08)',
                pointerEvents: 'none',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                position: 'relative',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(200,151,58,0.2)',
                  border: '2px solid rgba(200,151,58,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <User size={26} color="#c8973a" />
              </div>

              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: '22px',
                    fontWeight: 700,
                    color: 'white',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {displayName || stats?.profile?.name || '—'}
                </h2>
                {(displayRole || profile?.role_title) && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      marginTop: '5px',
                      padding: '3px 10px',
                      background: 'rgba(200,151,58,0.15)',
                      border: '1px solid rgba(200,151,58,0.3)',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#c8973a',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Shield size={10} />
                    {displayRole || profile?.role_title}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  flex: '1 1 220px',
                  justifyContent:
                    typeof window !== 'undefined' && window.innerWidth < 768
                      ? 'flex-start'
                      : 'flex-end',
                }}
              >
                {[
                  {
                    icon: <Hash size={12} />,
                    label: 'KGID',
                    value: displayKgid,
                  },
                  {
                    icon: <Briefcase size={12} />,
                    label: 'Department',
                    value: displayDepartment,
                  },
                  {
                    icon: <Building2 size={12} />,
                    label: 'Division',
                    value: displayDivision,
                  },
                ].map(({ icon, label, value }) =>
                  value ? (
                    <div
                      key={label}
                      style={{
                        padding: '8px 14px',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '10px',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '2px',
                          color: 'rgba(255,255,255,0.45)',
                          fontSize: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.07em',
                          fontWeight: 600,
                        }}
                      >
                        {icon} {label}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'white',
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stat tiles */}
        {statsQuery.isLoading ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '14px',
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  height: '110px',
                  animation: 'skeleton-pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        ) : statsQuery.isError ? (
          <ErrorState onRetry={() => statsQuery.refetch()} />
        ) : stats ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '14px',
              }}
            >
              {[
                // Approval stats are only meaningful to reviewers (records.approve).
                ...(canApprove
                  ? [
                      {
                        icon: <FileClock size={24} color="#d97706" />,
                        iconBg: '#fffbeb',
                        iconBorder: '#fde68a',
                        label: 'Pending Approvals',
                        value:
                          stats.upload_status?.pending ??
                          stats.pending_approvals ??
                          0,
                        routeKey: 'UPLOAD_STATUS',
                        accentColor: '#d97706',
                      },
                      {
                        icon: <FileCheck size={20} color="#16a34a" />,
                        iconBg: '#f0fdf4',
                        iconBorder: '#bbf7d0',
                        label: 'Approved Documents',
                        value: stats.upload_status?.approved ?? 0,
                        routeKey: 'UPLOAD_STATUS',
                        accentColor: '#16a34a',
                      },
                    ]
                  : []),
                {
                  icon: <KeyRound size={20} color="#7c3aed" />,
                  iconBg: '#faf5ff',
                  iconBorder: '#ddd6fe',
                  label: 'Pending Access Requests',
                  value: stats.access_status?.pending ?? 0,
                  routeKey: 'ACCESS_STATUS',
                  accentColor: '#7c3aed',
                },
                {
                  icon: <Bell size={20} color="#2563eb" />,
                  iconBg: '#eff6ff',
                  iconBorder: '#bfdbfe',
                  label: 'Unread Notifications',
                  value: stats.unread_notifications ?? 0,
                  routeKey: 'NOTIFICATIONS',
                  accentColor: '#2563eb',
                },
              ].map(
                ({
                  icon,
                  iconBg,
                  iconBorder,
                  label,
                  value,
                  routeKey,
                  accentColor,
                }) => (
                  <div
                    key={label}
                    onClick={() =>
                      navigate(ROUTES[routeKey as keyof typeof ROUTES])
                    }
                    style={{
                      background: 'white',
                      borderRadius: '14px',
                      padding: '20px',
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                      transition:
                        'box-shadow 0.15s, transform 0.15s',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform =
                        'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        '0 1px 4px rgba(0,0,0,0.05)';
                      e.currentTarget.style.transform =
                        'translateY(0)';
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: accentColor,
                        borderRadius: '14px 14px 0 0',
                      }}
                    />

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginTop: '4px',
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: '0 0 10px',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#94a3b8',
                            lineHeight: 1.25,
                          }}
                        >
                          {label}
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontFamily: 'DM Mono, monospace',
                            fontSize: '34px',
                            fontWeight: 600,
                            color: '#0f172a',
                            lineHeight: 1,
                          }}
                        >
                          {value}
                        </p>
                      </div>
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: iconBg,
                          border: `1px solid ${iconBorder}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '14px',
                        fontSize: '11px',
                        color: '#94a3b8',
                        fontWeight: 500,
                      }}
                    >
                      <TrendingUp size={11} />
                      <span>View details</span>
                      <ChevronRight size={11} />
                    </div>
                  </div>
                )
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '14px',
              }}
            >
              {[
                {
                  title: 'Total Documents',
                  description: 'All active documents',
                  stats: stats.global_documents,
                },
                {
                  title: 'Your Documents',
                  description: 'Documents you have uploaded',
                  stats: stats.user_documents,
                },
              ].map(({ title, description, stats: docStats }) => (
                <div
                  key={title}
                  style={{
                    background: 'white',
                    borderRadius: '14px',
                    border: '1px solid #e2e8f0',
                    padding: '18px 20px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '10px',
                      gap: '10px',
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '15px',
                          fontWeight: 700,
                          color: '#0f172a',
                        }}
                      >
                        {title}
                      </p>
                      <p
                        style={{
                          margin: '2px 0 0',
                          fontSize: '13px',
                          color: '#0f172a',
                        }}
                      >
                        {description}
                      </p>
                    </div>
                    <div
                      style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '12px',
                        background: '#eef2ff',
                        border: '1px solid #c7d2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FileText size={18} color="#4f46e5" />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: '10px',
                      marginTop: '8px',
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '15px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: '#9ca3af',
                          fontWeight: 600,
                        }}
                      >
                        Documents
                      </p>
                      <p
                        style={{
                          margin: '4px 0 0',
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#111827',
                        }}
                      >
                        {docStats?.total_documents ?? 0}
                      </p>
                    </div>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '15px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: '#9ca3af',
                          fontWeight: 600,
                        }}
                      >
                        Pages
                      </p>
                      <p
                        style={{
                          margin: '4px 0 0',
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#111827',
                        }}
                      >
                        {docStats?.total_pages ?? 0}
                      </p>
                    </div>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '15px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: '#9ca3af',
                          fontWeight: 600,
                        }}
                      >
                        Size
                      </p>
                      <p
                        style={{
                          margin: '4px 0 0',
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#111827',
                        }}
                      >
                        {formatFileSize(docStats?.total_size_bytes ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Latest files + favorites */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {/* Latest files */}
          <div
            style={{
              background: 'white',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                padding: '18px 20px 14px',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Clock size={16} color="#2563eb" />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#0f172a',
                  }}
                >
                  Latest Files
                </h3>
              </div>
              <Link
                to={ROUTES.LATEST_FILES}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  textDecoration: 'none',
                }}
              >
                View all <ChevronRight size={13} />
              </Link>
            </div>

            {latestFilesQuery.isLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '28px',
                }}
              >
                <Spinner size={18} />
              </div>
            ) : latestFilesQuery.isError ? (
              <ErrorState onRetry={() => latestFilesQuery.refetch()} />
            ) : latestFilesQuery.data?.data?.documents?.length ? (
              <div style={{ padding: '8px 0' }}>
                {latestFilesQuery.data.data.documents.map(
                  (doc: any, i: number) => {
                    const ext = (doc.original_file_name || '')
                      .split('.')
                      .pop()
                      ?.toLowerCase();
                    const FileIcon =
                      ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
                        ext || ''
                      )
                        ? Image
                        : ['xlsx', 'xls', 'csv'].includes(ext || '')
                        ? FileSpreadsheet
                        : FileText;
                    const list = latestFilesQuery.data.data
                      .documents as any[];
                    return (
                      <div
                        key={doc.document_id ?? i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 20px',
                          borderBottom:
                            i < list.length - 1
                              ? '1px solid #f8fafc'
                              : 'none',
                          transition: 'background 0.12s',
                          cursor: 'default',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            '#f8fafc')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            'transparent')
                        }
                      >
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '9px',
                            background: '#f1f5f9',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FileIcon
                            size={16}
                            color="#64748b"
                          />
                        </div>
                        <div
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatDocumentDisplayName({
                              cmsNumber: doc.case?.cms_number ?? doc.cms_number,
                              originalFileName: doc.original_file_name,
                              documentType: doc.document_type,
                            })}
                          </p>
                          <p
                            style={{
                              margin: '2px 0 0',
                              fontSize: '14px',
                              color: '#94a3b8',
                            }}
                          >
                            {(doc.case as any)?.fir_number ??
                              (doc as any).file_number ??
                              doc.case?.cms_number ??
                              doc.cms_number}
                            {doc.uploaded_at && (
                              <span>
                                {' '}
                                ·{' '}
                                {new Date(
                                  doc.uploaded_at
                                ).toLocaleDateString(
                                  'en-IN',
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                  }
                                )}
                              </span>
                            )}
                          </p>
                        </div>
                        {/* <div
                          style={{
                            padding: '2px 8px',
                            borderRadius: '999px',
                            flexShrink: 0,
                            fontSize: '14px',
                            fontWeight: 600,
                            background:
                              doc.approval_status ===
                              'Approved'
                                ? 'rgba(34,197,94,0.08)'
                                : 'rgba(234,179,8,0.1)',
                            color:
                              doc.approval_status ===
                              'Approved'
                                ? '#16a34a'
                                : '#92400e',
                            border: `1px solid ${
                              doc.approval_status ===
                              'Approved'
                                ? 'rgba(34,197,94,0.2)'
                                : 'rgba(234,179,8,0.25)'
                            }`,
                          }}
                        >
                          {doc.approval_status}
                        </div> */}
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <EmptyState
                title="No recent files"
                description="You haven't uploaded any documents recently."
              />
            )}
          </div>

          {/* Favorites */}
          <div
            style={{
              background: 'white',
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                padding: '18px 20px 14px',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#fff1f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Star size={16} color="#e11d48" />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#0f172a',
                  }}
                >
                  Favorites
                </h3>
              </div>
              <Link
                to={ROUTES.FAVORITES}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  textDecoration: 'none',
                }}
              >
                View all <ChevronRight size={13} />
              </Link>
            </div>

            {favoritesQuery.isLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '28px',
                }}
              >
                <Spinner size={18} />
              </div>
            ) : favoritesQuery.isError ? (
              <ErrorState
                onRetry={() => favoritesQuery.refetch()}
              />
            ) : favoritesQuery.data?.data?.favorites?.length ? (
              <div style={{ padding: '8px 0' }}>
                {favoritesQuery.data.data.favorites.map(
                  (doc: any, i: number) => {
                    const ext = (doc.original_file_name || '')
                      .split('.')
                      .pop()
                      ?.toLowerCase();
                    const FileIcon =
                      ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
                        ext || ''
                      )
                        ? Image
                        : ['xlsx', 'xls', 'csv'].includes(ext || '')
                        ? FileSpreadsheet
                        : FileText;
                    const list =
                      favoritesQuery.data.data
                        .favorites as any[];
                    return (
                      <div
                        key={doc.document_id ?? i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 20px',
                          borderBottom:
                            i < list.length - 1
                              ? '1px solid #f8fafc'
                              : 'none',
                          transition: 'background 0.12s',
                          cursor: 'default',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            '#f8fafc')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            'transparent')
                        }
                      >
                        <div
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '9px',
                            background: '#fff1f2',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FileIcon
                            size={16}
                            color="#e11d48"
                          />
                        </div>
                        <div
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#1e293b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatDocumentDisplayName({
                              cmsNumber: doc.case?.cms_number ?? doc.cms_number,
                              originalFileName: doc.original_file_name,
                              documentType: doc.document_type,
                            })}
                          </p>
                          <p
                            style={{
                              margin: '2px 0 0',
                              fontSize: '14px',
                              color: '#94a3b8',
                            }}
                          >
                            {(doc.case as any)?.fir_number ??
                              (doc as any).file_number ??
                              doc.case?.cms_number ??
                              doc.cms_number}
                            {doc.favorited_at && (
                              <span>
                                {' '}
                                ·{' '}
                                {new Date(
                                  doc.favorited_at
                                ).toLocaleDateString(
                                  'en-IN',
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                  }
                                )}
                              </span>
                            )}
                          </p>
                        </div>
                        <Star
                          size={14}
                          fill="#fbbf24"
                          color="#fbbf24"
                        />
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <EmptyState
                title="No favorites yet"
                description="Mark frequently used documents as favorites to see them here."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
